import { hasValue } from 'swipeclock-frontend';

const RTK_TRANSPORT_STATUSES = new Set(['FETCH_ERROR', 'TIMEOUT_ERROR']);

const RTK_TRANSIENT_STATUSES = new Set([
  ...RTK_TRANSPORT_STATUSES,
  'PARSING_ERROR',
]);

const getErrorStatus = (error) => {
  const status = error?.status;
  const originalStatus = error?.originalStatus;

  if (typeof status === 'number') {
    return status;
  }

  if (typeof originalStatus === 'number') {
    return originalStatus;
  }

  return status ?? originalStatus;
};

/**
 * Returns true when an RTK Query error represents an authorization failure
 * (expired or invalid JWT) that should pause queue drain until a new token
 * is available.
 */
export function isAuthError(error) {
  const status = getErrorStatus(error);

  return status === 401 || status === 403;
}

/**
 * Returns true when the request never received an HTTP response.
 * Timeouts and fetch failures mean the API cannot currently be reached.
 * RTK Query 1.9 maps a timed-out abort to TIMEOUT_ERROR, so a bare
 * AbortError from cancellation is not treated as an outage.
 */
export function isTransportError(error) {
  if (!error) {
    return false;
  }

  return RTK_TRANSPORT_STATUSES.has(getErrorStatus(error));
}

/**
 * Returns true when an RTK Query error represents a transient network/outage
 * condition eligible for offline queueing or automatic retry.
 */
export function isNetworkError(error) {
  if (!error) {
    return false;
  }

  if (isTransportError(error)) {
    return true;
  }

  const status = getErrorStatus(error);

  if (RTK_TRANSIENT_STATUSES.has(status)) {
    return true;
  }

  if (typeof status === 'number' && status >= 500) {
    return true;
  }

  return false;
}

/**
 * Returns true when an RTK Query error represents a business-rule rejection
 * (not a transient network failure).
 */
export function isBusinessError(error) {
  if (!error || isNetworkError(error) || isAuthError(error)) {
    return false;
  }

  const status = getErrorStatus(error);

  if (typeof status === 'number' && status >= 400 && status < 500) {
    return true;
  }

  const data = error.data ?? error?.response?.data;
  const screenName = data?.screenName ?? data?.RuleResult?.screenName;

  return hasValue(screenName);
}
